
import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, Plane, FileText, Users, Mail, Shield, Globe } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ModernFAQSection = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const faqItems = [
    {
      id: 1,
      icon: Plane,
      title: "¿Cómo empiezo a usar Migrante Amigo?",
      content: "Solo entra al sitio, selecciona el tema que te interesa y escribe tu pregunta. Nuestro asistente virtual te dará una respuesta clara en segundos."
    },
    {
      id: 2,
      icon: Shield,
      title: "¿Mi información está protegida?",
      content: "Sí. Migrante Amigo no guarda tus preguntas ni tu información personal. Toda la interacción es privada y segura."
    },
    {
      id: 3,
      icon: FileText,
      title: "¿Cuánto cuesta usar Migrante Amigo?",
      content: "Puedes hacer preguntas de forma gratuita. Algunas funciones avanzadas pueden tener un costo más adelante, pero siempre se te notificará antes de pagar."
    },
    {
      id: 4,
      icon: Users,
      title: "¿Quién me ayuda cuando tengo dudas?",
      content: "Las respuestas son generadas por nuestro asistente digital, entrenado con información confiable sobre procesos migratorios. No reemplaza el consejo de un abogado, pero te orienta de forma clara y útil."
    },
    {
      id: 5,
      icon: Globe,
      title: "¿Puedo usar Migrante Amigo si estoy fuera de los Estados Unidos?",
      content: "Sí. Puedes acceder desde cualquier país mientras tengas conexión a internet."
    }
  ];

  const toggleCard = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4">
          Respuestas a tus preguntas frecuentes
        </h2>
        <p className="text-coolGray text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
          Aquí resolvemos tus dudas más comunes sobre Migrante Amigo y cómo podemos ayudarte con tus trámites migratorios.
        </p>
      </div>

      {/* FAQ Cards */}
      <div className="space-y-6 mb-12">
        {faqItems.map((item) => {
          const IconComponent = item.icon;
          const isExpanded = expandedCard === item.id;
          
          return (
            <Card 
              key={item.id}
              className="cursor-pointer transition-all duration-300 hover:shadow-xl hover:bg-slate-50/50 border border-gray-100 shadow-md bg-white/95 backdrop-blur-sm"
              onClick={() => toggleCard(item.id)}
            >
              <CardContent className="p-5 md:p-6">
                <div className="flex items-center justify-between min-h-[44px]">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center shadow-sm">
                        <IconComponent className="h-6 w-6 text-teal" />
                      </div>
                    </div>
                    <h3 className="text-lg md:text-xl font-medium text-navy leading-tight">
                      {item.title}
                    </h3>
                  </div>
                  
                  <div className="flex-shrink-0 ml-4">
                    <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="h-5 w-5 text-coolGray" />
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="mt-6 pl-16 pr-4 animate-accordion-down">
                    <div className="p-5 bg-teal/5 rounded-xl border-l-4 border-teal shadow-sm">
                      <p className="text-coolGray leading-relaxed text-base">
                        {item.content}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Contact CTA Box */}
      <Card className="bg-gradient-to-br from-slate-50 to-blue-50/50 border border-blue-100/50 shadow-lg">
        <CardContent className="p-8 text-center">
          <h3 className="text-xl md:text-2xl font-semibold text-navy mb-4">
            ¿Aún tienes preguntas?
          </h3>
          <p className="text-coolGray text-base md:text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
            Contáctanos directamente y con gusto te ayudaremos con tu proceso migratorio.
          </p>
          <Button 
            className="bg-teal hover:bg-teal-hover text-white px-8 py-3 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contáctanos
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernFAQSection;
